import { ArrowRight, ShieldCheck, Heart, MessageCircle } from "lucide-react";
import { Reveal, ChapterLabel, scrollTo } from "./Motion";
import { COMMUNITY_CARDS } from "../../data/content";

export const Ch4Community = () => (
  <section id="chapter-4" className="bg-cream py-24 sm:py-36 overflow-hidden">
    <div className="mx-auto max-w-[1400px] px-6 sm:px-10">
      <Reveal><ChapterLabel index="Chapter Four" title="Community" /></Reveal>
      <Reveal delay={0.1}>
        <h2 className="serif-display mt-8 max-w-3xl text-[2rem] sm:text-[2.7rem] lg:text-[3.4rem] font-normal text-charcoal leading-[1.1]">
          A Community for Every Chapter of Life.
        </h2>
      </Reveal>
      <Reveal delay={0.15}>
        <p className="mt-6 max-w-2xl text-lg font-normal sm:font-light leading-relaxed text-ink/75">
          Life doesn't happen in one season. Whether you're celebrating, healing, parenting, grieving, growing, starting over, building a business, caring for family, navigating relationships, or simply looking for people who understand — you belong here. Every community inside HEARD exists to remind people they don't have to walk through life alone.
        </p>
      </Reveal>
      <Reveal delay={0.18}>
        <p className="mt-6 inline-flex items-center gap-2 rounded-full bg-sage/10 px-4 py-2 text-xs font-medium uppercase tracking-[0.16em] text-sage-deep" data-testid="anonymity-note">
          <ShieldCheck size={15} strokeWidth={1.9} className="text-sage" />
          Everyone shares under a private username — never your real name
        </p>
      </Reveal>

      <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-7">
        {COMMUNITY_CARDS.map((c, i) => (
          <Reveal key={c.name} delay={(i % 3) * 0.07}>
            <article
              data-testid={`community-${c.name.toLowerCase().replace(/['’]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}
              className="group flex h-full flex-col overflow-hidden rounded-[1.5rem] bg-white border border-line shadow-[0_10px_30px_rgba(47,75,57,0.05)] transition-all duration-500 hover:-translate-y-1.5 hover:shadow-[0_24px_54px_rgba(47,75,57,0.13)]"
            >
              <div className="relative overflow-hidden">
                <img
                  src={c.img}
                  alt={`The HEARD ${c.name} community`}
                  loading="lazy"
                  className="aspect-[16/10] w-full object-cover transition-transform [transition-duration:900ms] ease-out group-hover:scale-[1.06]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-charcoal/35 to-transparent" />
                <div className="absolute left-4 bottom-4 flex items-center gap-2.5">
                  <span className="flex h-9 w-9 items-center justify-center rounded-full bg-cream/90 text-sage-deep backdrop-blur">
                    <c.icon size={17} strokeWidth={1.8} />
                  </span>
                  <span className="font-serif text-xl font-semibold text-cream drop-shadow">{c.name}</span>
                </div>
              </div>

              <div className="flex flex-1 flex-col p-5 sm:p-6">
                <p className="text-base sm:text-sm leading-relaxed text-ink/70">{c.desc}</p>
                <p className="mt-2.5 flex items-center gap-1.5 text-xs text-ink/50">
                  <span className="h-1.5 w-1.5 rounded-full bg-sage" />
                  {(1.2 + (i * 0.37) % 3.4).toFixed(1)}k members · {24 + (i * 11) % 70} online
                </p>
                <div className="mt-4 rounded-2xl border border-line bg-cream-deep/40 p-4" data-testid={`post-preview-${c.user.toLowerCase()}`}>
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-sm font-semibold text-sage-deep">{c.user}</span>
                    <span className="text-[0.7rem] uppercase tracking-[0.12em] text-taupe">{c.time}</span>
                  </div>
                  <p className="mt-2 text-[0.95rem] sm:text-sm leading-relaxed text-ink/80">{c.preview}</p>
                  <div className="mt-3.5 flex items-center gap-5 text-xs text-ink/55">
                    <span className="inline-flex items-center gap-1.5">
                      <Heart size={14} className="text-sage fill-sage/30" strokeWidth={1.8} />
                      {c.likes}
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                      <MessageCircle size={14} strokeWidth={1.8} />
                      {c.comments}
                    </span>
                  </div>
                </div>
                <button
                  onClick={() => scrollTo("beta")}
                  data-testid={`explore-${c.name.toLowerCase().replace(/['’]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}
                  className="group/btn mt-4 -mb-1 inline-flex items-center gap-2 self-start py-2 text-sm font-medium text-sage-deep transition-colors hover:text-sage"
                >
                  Explore Community
                  <ArrowRight size={15} className="transition-transform duration-500 group-hover/btn:translate-x-1" />
                </button>
              </div>
            </article>
          </Reveal>
        ))}
      </div>

      <Reveal delay={0.2}>
        <p className="mt-16 text-sm uppercase tracking-[0.24em] text-taupe">And more communities, added gently over time.</p>
      </Reveal>
    </div>
  </section>
);
